import { add, cross, dot, normalize, scale, sub, type Vec3, v3 } from "./vec.ts";

export interface SegmentClosest {
  /** Parameters along each segment, 0..1. */
  s: number;
  t: number;
  c1: Vec3;
  c2: Vec3;
  dist2: number;
}

/** Closest points between segments p1–q1 and p2–q2 (Ericson, Real-Time Collision Detection 5.1.9). */
export function closestPointsSegments(p1: Vec3, q1: Vec3, p2: Vec3, q2: Vec3): SegmentClosest {
  const EPS = 1e-9;
  const d1 = sub(q1, p1);
  const d2 = sub(q2, p2);
  const r = sub(p1, p2);
  const a = dot(d1, d1);
  const e = dot(d2, d2);
  const f = dot(d2, r);
  let s = 0;
  let t = 0;
  if (a <= EPS && e <= EPS) {
    s = 0;
    t = 0;
  } else if (a <= EPS) {
    s = 0;
    t = clamp01(f / e);
  } else {
    const c = dot(d1, r);
    if (e <= EPS) {
      t = 0;
      s = clamp01(-c / a);
    } else {
      const b = dot(d1, d2);
      const denom = a * e - b * b;
      s = denom !== 0 ? clamp01((b * f - c * e) / denom) : 0;
      t = (b * s + f) / e;
      if (t < 0) {
        t = 0;
        s = clamp01(-c / a);
      } else if (t > 1) {
        t = 1;
        s = clamp01((b - c) / a);
      }
    }
  }
  const c1 = add(p1, scale(d1, s));
  const c2 = add(p2, scale(d2, t));
  const d = sub(c1, c2);
  return { s, t, c1, c2, dist2: dot(d, d) };
}

export interface Contact {
  /** Unit normal pointing from volume B toward volume A. */
  normal: Vec3;
  /** Contact point on B's surface. */
  point: Vec3;
  /** Penetration depth (> 0 when overlapping). */
  depth: number;
  /** Parameter along B's axis where contact happened, 0..1. */
  t: number;
}

/** Capsule A (a1–b1, r1) versus capsule B (a2–b2, r2). Spheres are capsules with a == b. */
export function capsuleContact(
  a1: Vec3,
  b1: Vec3,
  r1: number,
  a2: Vec3,
  b2: Vec3,
  r2: number,
): Contact | null {
  const c = closestPointsSegments(a1, b1, a2, b2);
  const rr = r1 + r2;
  if (c.dist2 >= rr * rr) return null;
  const dist = Math.sqrt(c.dist2);
  let normal: Vec3;
  if (dist > 1e-9) {
    normal = scale(sub(c.c1, c.c2), 1 / dist);
  } else {
    // Axes intersect: pick a normal perpendicular to B's axis, falling back to +y.
    const axis = sub(b2, a2);
    const n = cross(axis, v3(0, 1, 0));
    normal = dot(n, n) > 1e-12 ? normalize(n) : v3(0, 1, 0);
  }
  return { normal, point: add(c.c2, scale(normal, r2)), depth: rr - dist, t: c.t };
}

/**
 * Two-bone IK: returns the mid joint for a chain root → mid → end with bone
 * lengths l1, l2, bending toward `pole` (a direction, fighter space or world).
 */
export function twoBoneIk(root: Vec3, target: Vec3, l1: number, l2: number, pole: Vec3): Vec3 {
  const toTarget = sub(target, root);
  let d = Math.sqrt(dot(toTarget, toTarget));
  const minD = Math.abs(l1 - l2) + 1e-4;
  const maxD = l1 + l2 - 1e-4;
  if (d < minD) d = minD;
  if (d > maxD) d = maxD;
  const u = normalize(toTarget);
  // Distance from root to the mid joint's projection on the root→target axis.
  const a = (l1 * l1 - l2 * l2 + d * d) / (2 * d);
  const h2 = l1 * l1 - a * a;
  const h = h2 > 0 ? Math.sqrt(h2) : 0;
  let p = sub(pole, scale(u, dot(pole, u)));
  if (dot(p, p) < 1e-12) {
    // Pole parallel to the axis: pick any perpendicular.
    const alt = Math.abs(u.y) < 0.9 ? v3(0, 1, 0) : v3(1, 0, 0);
    p = sub(alt, scale(u, dot(alt, u)));
  }
  p = normalize(p);
  return add(add(root, scale(u, a)), scale(p, h));
}

function clamp01(x: number): number {
  return x < 0 ? 0 : x > 1 ? 1 : x;
}
